<template>
  <Card class="space-y-4">
    <Skeleton v-if="userRatingPending" class="h-24" />
    <div v-else-if="userRating && !editing">
      <div class="mb-2 text-sm text-neutral-600 dark:text-neutral-400">
        Your review
      </div>
      <ReviewCard
        :rating="userRating"
        showEdit
        showDelete
        @edit="editing = true"
        @delete="
          () => {
            showConfirmDeleteModal = true;
            ratingToDelete = userRating;
          }
        "
      />
    </div>
    <TitleReviewAddEditForm
      v-else-if="editing || !userRating"
      v-model:review-form="userReview"
      @cancel="editing = false"
      @submit="submitReview"
      :disabled="submitPending"
      :loading="submitPending"
    />
    <NamiDivider v-if="ratingsPending || allRatings?.data.length" />
    <NamiLoading v-if="ratingsPending" />
    <div v-else-if="allRatings?.data.length" class="space-y-4">
      <ReviewCard
        v-for="review in allRatings.data.filter(
          (rating) => rating.relationships.find(isUser)?.id !== userEntity?.id,
        )"
        :highlightUser="
          orgMembers?.includes(review.relationships.find(isUser)?.id ?? '')
        "
        :rating="review"
        :showDelete="isStaff"
        @delete="
          () => {
            showConfirmDeleteModal = true;
            ratingToDelete = review;
          }
        "
      />
      <div v-if="allRatings.data.length === 0 && userRating">
        You're the only reviewer!
      </div>
      <div v-else-if="allRatings.data.length === 0">No Reviews</div>
    </div>
  </Card>
  <NamiModalPresets
    v-model="showConfirmDeleteModal"
    :preventOutsideClicks="actionPending"
    :options="{
      title: 'Delete this review?',
      icon: 'confused',
      detail: 'Are you sure you want to permamently delete this review?',
      buttons: [
        {
          buttonType: 'secondary',
          buttonText: 'Cancel',
          onButtonClick: () => {
            showConfirmDeleteModal = false;
            ratingToDelete = undefined;
          },
          disabled: actionPending,
        },
        {
          buttonType: 'danger',
          buttonText: 'Delete',
          onButtonClick: doDelete,
          disabled: actionPending,
        },
      ],
    }"
  />
</template>

<script setup lang="ts">
import {
  getId,
  isCreator,
  isMember,
  isOrg,
  isUser,
  Organization,
  Rating,
  type RatingEntity,
  type TitleEntity,
} from "~/src/api";
import type { TitleReviewForm } from "~/types/forms/titleReview";
import {
  convertReviewToForm,
  getDefaultReviewForm,
} from "~/src/forms/titleReview/common";
import { updateReview } from "~/src/forms/titleReview/update";
import { createReview } from "~/src/forms/titleReview/create";

const route = useRoute();
const nuxtApp = useNuxtApp();
const locale = nuxtApp.$i18n.global.locale;
const authStore = nuxtApp.$auth();
const appStore = nuxtApp.$app();

const { titleEntity } = defineProps<{
  titleEntity: TitleEntity;
}>();
const userEntity = computed(() => authStore?.userEntity);

const isStaff = computed(() => hasAdminAccess(authStore?.user));

const showConfirmDeleteModal = ref(false);

const userReview = ref<TitleReviewForm>(
  getDefaultReviewForm(titleEntity.id, locale.value),
);

const setRatingEntity = (entity: RatingEntity) => {
  userReview.value = convertReviewToForm(entity);
  editing.value = false;
  userRating.value = entity;
};

const ratingToDelete = ref<RatingEntity>();

const editing = ref(false);
const actionPending = ref(false);

const { data: orgMembers, pending } = useAsyncData(
  `org-member-ids-${titleEntity.relationships.find(isOrg)?.id}`,
  async () => {
    const orgId = titleEntity.relationships.find(isOrg)?.id;
    if (!orgId) return [];
    const res = await Organization.get(orgId);
    return res.relationships
      .filter((rel) => isUser(rel) || isCreator(rel) || isMember(rel))
      .map(getId);
  },
);

const { data: allRatings, pending: ratingsPending } = useAsyncData(
  `ratings-${titleEntity.id}`,
  async () => {
    if (!titleEntity.id) return;
    return await Rating.search({
      titleId: titleEntity.id,
      includes: ["user", "profile_frame", "badge"],
    });
  },
);

const { data: userRating, pending: userRatingPending } = useAsyncData(
  async () => {
    await authStore?.waitUntilRefreshIsComplete();
    const user = userEntity.value;
    const token = (await authStore?.getToken()) || undefined;
    if (!user) return;
    const res = await Rating.search(
      {
        userId: user.id,
        titleId: titleEntity.id,
        includes: ["user"],
      },
      token,
    );

    const review = res.data.at(0);
    if (review) setRatingEntity(review);
    return review;
  },
  { server: false, default: () => undefined },
);

const { action: submitReview, pending: submitPending } = useAction2(
  async () => {
    await executeWithNotificationOnError(async () => {
      const user = authStore?.userEntity;
      if (!user) throw new Error("Not logged in.");

      let newEntity: RatingEntity;

      // we need to edit their existing review
      if (userReview.value.reviewMeta) {
        appStore?.notify({
          preset: "loading.plain",
          title: "Updating Review",
          timer: 3000,
        });

        newEntity = await updateReview(userReview.value);
      } else {
        // otherwise we create a new one
        appStore?.notify({
          preset: "loading.plain",
          title: "Posting Review",
          timer: 3000,
        });

        newEntity = await createReview(userReview.value);
      }

      newEntity.relationships = [{ ...user }];
      setRatingEntity(newEntity);

      appStore?.closeAllNotifications();
      appStore?.notify({
        preset: "success.plain",
        title: "Review posted!",
        timer: 3000,
      });

      editing.value = false;
    });
  },
);

async function doDelete() {
  assertDefined(appStore);
  const token = await authStore?.getToken();

  const { close } = await appStore.notify({
    preset: "loading.plain",
    detail: "Deleting review",
  });

  try {
    if (!token) {
      throw new Error("Not logged in.");
    }
    if (!ratingToDelete.value) {
      throw new Error("No rating selected.");
    }

    actionPending.value = true;

    const { id, attributes } = ratingToDelete.value;

    const result = await Rating.delete(id, attributes.version, token);
    if (!result) {
      throw new Error(`Could not delete review.`);
    }

    if (ratingToDelete.value.id === userRating.value?.id) {
      userReview.value = getDefaultReviewForm(titleEntity.id, locale.value);
      editing.value = true;
    } else {
      allRatings.value?.data.splice(
        allRatings.value.data.findIndex(
          (review) => review.id === ratingToDelete.value?.id,
        ),
        1,
      );
    }
  } catch (e) {
    const { title, detail } = resolveError(e);

    appStore?.notify({
      preset: "error.plain",
      title,
      detail,
      timer: 5000,
    });
  }

  close();
  showConfirmDeleteModal.value = false;
  actionPending.value = false;
}
</script>
